import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  Autocomplete,
  Chip,
  Divider,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  DeleteOutline as DeleteOutlineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useLead } from '../../contexts/LeadContext';
import { useModal } from '../../contexts/ModalContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  ink2: '#253b2c',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  redLt: '#fef2f2',
  redBorder: '#fecaca',
  redMd: '#b91c1c',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
    '&.Mui-error fieldset': { borderColor: T.red },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
    '&.Mui-error': { color: T.red },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: T.font,
    fontSize: 11.5,
    marginTop: 0.5,
  },
};

const LeadDelete = ({
  open,
  onClose,
  leadIds = [],
  leadName = '',
  leadId = '',
  leadDetails = [], // passed for bulk flows; reserved for future detail preview
  onComplete = () => {},
}) => {
  const { deleteLead } = useLead();
  const { openErrorModal } = useModal();

  const LEAD_DELETE_REASONS = [
    'Customer chose competitor',
    'Budget constraints',
    'Requirements changed',
    'Location preference changed',
    'Timing not right for customer',
    'Not interested anymore',
    'Found another property',
    'Personal circumstances changed',
    'Decided to continue renting',
    'Financing not approved',
    'Lead added by mistake',
    'Duplicate entry – added in error',
    'Incorrect lead creation',
    'Lead was added unintentionally',
    'Erroneous lead entry',
  ];

  const [selectedReason, setSelectedReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [deletionResults, setDeletionResults] = useState({ passed: 0, errored: 0, results: [] });
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(null);
  const [showReasonError, setShowReasonError] = useState(false);

  const handleReasonChange = (event, newValue) => {
    setSelectedReason(newValue || '');
    setShowReasonError(false);
  };

  const handleAdditionalNotesChange = (e) => {
    setAdditionalNotes(e.target.value);
  };

  const handleDelete = async () => {
    if (!selectedReason) {
      setShowReasonError(true);
      enqueueSnackbar('Please select a reason for deleting', { variant: 'error' });
      return;
    }

    const finalNotes = additionalNotes
      ? `${selectedReason} - ${additionalNotes}`
      : selectedReason;

    const ids = leadId ? [leadId] : leadIds;

    if (!ids.length) {
      enqueueSnackbar('No leads selected for deletion', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        lead_ids: ids,
        notes: finalNotes,
      };

      const response = await deleteLead(payload);
      if (response.status !== 200) {
        openErrorModal(
          'Delete Leads',
          response.data.message || 'Failed to delete lead(s)'
        );
        return;
      }
      const data = response.data;
      const passed_count = data.passed || 0;
      const errored_count = data.errored || 0;
      const results = data.results || [];
      setDeletionResults({
        passed: passed_count,
        errored: errored_count,
        results: results,
      });
      setError(null);
      setShowResults(true);
      onClose();
    } catch (err) {
      let result = { passed: 0, errored: 0, results: [] };
      let hasResults = false;

      if (err.response && err.response.data) {
        try {
          result = err.response.data;
          hasResults = result.passed > 0 || result.errored > 0;
          if (hasResults) {
            setDeletionResults({
              passed: result.passed,
              errored: result.errored,
              results: result.results,
            });
            if (result.passed > 0) {
              enqueueSnackbar(`Partially successful: ${result.passed} lead(s) deleted`, { variant: 'warning' });
            }
            setError(null);
            setShowResults(true);
            onClose();
            return;
          }
          setError(err.message || 'Failed to delete lead(s)');
          enqueueSnackbar(err.message || 'Failed to delete lead(s)', { variant: 'error' });
        } catch (parseError) {
          setError(err.message || 'Failed to delete lead(s)');
          enqueueSnackbar(err.message || 'Failed to delete lead(s)', { variant: 'error' });
        }
      } else {
        setError(err.message || 'Failed to delete lead(s)');
        enqueueSnackbar(err.message || 'Failed to delete lead(s)', { variant: 'error' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseResults = () => {
    setShowResults(false);
    onComplete();
    onClose();
  };

  useEffect(() => {
    if (open) {
      setSelectedReason('');
      setAdditionalNotes('');
      setError(null);
      setShowReasonError(false);
      setShowResults(false);
      setDeletionResults({ passed: 0, errored: 0, results: [] });
    }
  }, [open]);

  const isBulkMode = leadIds.length > 1;

  if (showResults) {
    const successCount = deletionResults.passed || 0;
    const failedCount = deletionResults.errored || 0;

    return (
      <Dialog
        open={showResults}
        onClose={handleCloseResults}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: T.font,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 2,
              borderBottom: `1px solid ${T.borderL}`,
              bgcolor: T.bg,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  bgcolor: successCount > 0 ? T.greenLt : T.redLt,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {successCount > 0 ? (
                  <CheckCircleIcon sx={{ fontSize: 22, color: T.green }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 22, color: T.red }} />
                )}
              </Box>
              <Box>
                <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink }}>
                  Deletion results
                </Typography>
                <Typography sx={{ fontFamily: T.font, fontSize: 12, color: T.muted, mt: 0.25 }}>
                  Summary of each lead processed
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleCloseResults} aria-label="Close" sx={{ color: T.muted }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 2.5 }}>
          <Stack spacing={2.5}>
            <Alert
              severity={successCount > 0 ? 'success' : 'error'}
              icon={successCount > 0 ? <CheckCircleIcon /> : <ErrorIcon />}
              sx={{
                fontFamily: T.font,
                borderRadius: 2,
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography sx={{ fontFamily: T.font, fontSize: 14, fontWeight: 600 }}>
                {successCount > 0
                  ? `Successfully deleted ${successCount} lead${successCount !== 1 ? 's' : ''}`
                  : `Failed to delete ${failedCount} lead${failedCount !== 1 ? 's' : ''}`}
                {failedCount > 0 && successCount > 0 && `, ${failedCount} lead${failedCount !== 1 ? 's' : ''} failed`}
              </Typography>
            </Alert>

            {successCount > 0 && (
              <Box>
                <Typography sx={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.green, mb: 1 }}>
                  Successfully deleted
                </Typography>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    border: `1px solid ${T.borderL}`,
                    borderRadius: 2,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: T.greenLt }}>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Lead ID</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Name</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Contact</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deletionResults.results
                        .filter((lead) => lead.message?.includes('successfully'))
                        .map((lead) => (
                          <TableRow key={lead.lead_id} sx={{ '&:nth-of-type(odd)': { bgcolor: T.bg } }}>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13, fontWeight: 600 }}>#{lead.lead_id}</TableCell>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13 }}>{lead.name}</TableCell>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13 }}>{lead.contact}</TableCell>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13, color: T.greenMd }}>{lead.message}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {failedCount > 0 && (
              <Box>
                <Typography sx={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.red, mb: 1 }}>
                  Failed to delete
                </Typography>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    border: `1px solid ${T.redBorder}`,
                    borderRadius: 2,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: T.redLt }}>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Lead ID</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Name</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Contact</TableCell>
                        <TableCell sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 12, color: T.ink2 }}>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deletionResults.results
                        .filter((lead) => !lead.message?.includes('successfully'))
                        .map((lead) => (
                          <TableRow key={lead.lead_id} sx={{ '&:nth-of-type(odd)': { bgcolor: T.bg } }}>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13, fontWeight: 600 }}>#{lead.lead_id}</TableCell>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13 }}>{lead.name}</TableCell>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13 }}>{lead.email}</TableCell>
                            <TableCell sx={{ fontFamily: T.font, fontSize: 13, color: T.redMd }}>{lead.message}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {successCount === 0 && failedCount === 0 && (
              <Alert severity="info" sx={{ fontFamily: T.font, borderRadius: 2 }}>
                No leads were processed for deletion.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: `1px solid ${T.borderL}`,
            bgcolor: T.bg,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            onClick={handleCloseResults}
            variant="contained"
            sx={{
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: 2.5,
              bgcolor: T.green,
              boxShadow: 'none',
              '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const title = isBulkMode ? 'Bulk delete leads' : 'Delete lead';
  const subtitle = isBulkMode
    ? `${leadIds.length} leads will be removed from the list`
    : 'This action can be undone from restore';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="lead-delete-dialog-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          width: '100%',
          borderRadius: 3,
          fontFamily: T.font,
          overflow: 'hidden',
          m: 2,
        },
      }}
    >
      <DialogTitle id="lead-delete-dialog-title" sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: T.redLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 22, color: T.red }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
                {title}
              </Typography>
              <Typography sx={{ fontFamily: T.font, fontSize: 11.5, color: T.muted, mt: 0.25 }}>
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: T.muted, '&:hover': { color: T.ink } }} disabled={isSubmitting}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: T.borderL }} />

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${T.borderL}`,
            bgcolor: T.bg,
          }}
        >
          {isBulkMode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: 13, color: T.ink2 }}>Selected</Typography>
              <Chip
                label={`${leadIds.length} leads`}
                size="small"
                sx={{
                  fontFamily: T.font,
                  fontWeight: 600,
                  fontSize: 12,
                  bgcolor: T.greenLt,
                  color: T.green,
                  border: `1px solid ${T.border}`,
                }}
              />
            </Box>
          ) : (
            <Typography sx={{ fontFamily: T.font, fontSize: 13, color: T.ink2 }}>
              <Box component="span" sx={{ color: T.muted, fontWeight: 600, mr: 0.5 }}>
                Lead
              </Box>
              <Box component="span" sx={{ fontWeight: 600, color: T.ink }}>{leadName || '—'}</Box>
            </Typography>
          )}
          <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.subtle, mt: 1.25, lineHeight: 1.5 }}>
            {isBulkMode
              ? 'These leads will be soft-deleted. You can restore them anytime from the appropriate view.'
              : 'This lead will be soft-deleted. You can restore it anytime.'}
          </Typography>
        </Box>

        <Autocomplete
          id="archive-reason"
          options={LEAD_DELETE_REASONS}
          fullWidth
          value={selectedReason || null}
          onChange={handleReasonChange}
          sx={{ mb: 0.5 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Reason for deleting"
              required
              variant="outlined"
              error={showReasonError && !selectedReason}
              helperText={showReasonError && !selectedReason ? 'Please select a reason' : ' '}
              sx={fieldSx}
            />
          )}
        />

        <TextField
          id="notes"
          label="Additional notes"
          type="text"
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          value={additionalNotes}
          onChange={handleAdditionalNotesChange}
          variant="outlined"
          placeholder="Optional — extra context for your team"
          sx={{ ...fieldSx, mt: 0.5 }}
        />

        {error && typeof error === 'string' && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: `1px solid ${T.redBorder}`, bgcolor: T.redLt }}>
            <Typography sx={{ fontFamily: T.font, fontSize: 12.5, color: T.red }}>{error}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
          borderTop: `1px solid ${T.borderL}`,
          bgcolor: T.bg,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 500,
            color: T.subtle,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          disabled={isSubmitting || !selectedReason}
          endIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            fontFamily: T.font,
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2.5,
            bgcolor: T.red,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { bgcolor: T.redMd, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
          }}
        >
          {isSubmitting
            ? isBulkMode
              ? 'Deleting leads…'
              : 'Deleting…'
            : isBulkMode
              ? 'Delete leads'
              : 'Delete lead'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadDelete;
